import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";

import MapContainer from "../MapContainer";
import AdvancedMarker from "../AdvancedMarker";
import EnvironmentOverlay from "./EnvironmentOverlay";
import EnvironmentLegend from "./EnvironmentLegend";
import GroupAirQualityMapKey from "./GroupAirQualityMapKey";
import MetricButtons from "./MetricButtons";
import {
    airMetricConfig,
    airMetricOrder,
    NO_DATA_COLOR,
    getMetricColor,
    hexToRgb,
} from "../../constants/airQuality";
import { aggregate, buildJoins, buildSegments } from "../../services/environmentAggregation";
import startIcon from "../../images/icons/pin-start.svg";
import finishIcon from "../../images/icons/pin-stop.svg";

/** Esiti per cui il campione non ha un pallino: posizione non nota o presa in sosta. */
const HIDDEN_REJECTIONS = ["stationary", "uncertainPosition"];

/**
 * Aggregazione della "qualità dell'aria di gruppo" (dashboard e Open Data): per ogni grandezza i
 * campioni di `data.samples` sui pezzi del percorso di gruppo `data.pieces`, la grandezza scelta e
 * quanto serve alla mappa. `results` e' l'unico insieme di risultati, usato anche dal CSV.
 */
export function useGroupAirQualityAggregation(data) {
    const { t, i18n } = useTranslation();
    const [selectedMetricKey, setSelectedMetricKey] = useState(airMetricOrder[0]);

    const results = useMemo(() => {
        const out = {};
        airMetricOrder.forEach(k => {
            out[k] = data ? aggregate(data.pieces, data.samples, k) : null;
        });
        return out;
    }, [data]);

    const countByMetric = useMemo(() => {
        const out = {};
        airMetricOrder.forEach(k => {
            out[k] = results[k] ? results[k].usedCount : 0;
        });
        return out;
    }, [results]);

    const availableMetrics = airMetricOrder.filter(k => results[k] && results[k].hasData);

    useEffect(() => {
        if (availableMetrics.length > 0 && !availableMetrics.includes(selectedMetricKey)) {
            setSelectedMetricKey(availableMetrics[0]);
        }
    }, [availableMetrics, selectedMetricKey]);

    const activeConfig = airMetricConfig[selectedMetricKey];
    const metricLabel = t(activeConfig.titleTranslationKey);
    const unit = activeConfig.symbol;
    const result = results[selectedMetricKey];

    // Una linea per pezzo, mai unita a un altro pezzo, piu' i raccordi corti fra i pezzi.
    const segments = useMemo(
        () => (result ? [...buildSegments(result.pieces), ...buildJoins(result.pieces)] : []),
        [result]
    );

    // Niente pallino per i campioni senza posizione o presi in sosta; grigio per i fuori tetto e
    // i fuori scala; i campioni stimati li disegna vuoti l'overlay.
    const dots = useMemo(() => {
        if (!result) return [];
        return result.samples
            .filter(s => !HIDDEN_REJECTIONS.includes(s.rejected))
            .map(s => ({
                ...s,
                color: s.rejected ? hexToRgb(NO_DATA_COLOR) : hexToRgb(getMetricColor(selectedMetricKey, s.value)),
            }));
    }, [result, selectedMetricKey]);

    const rejectedLabel = reason => {
        if (reason === "implausible") return t("groupAirQualityRejectedImplausible", "fuori tetto di plausibilità");
        if (reason === "outlier") return t("groupAirQualityRejectedOutlier", "fuori scala rispetto al gruppo");
        if (reason === "uncertainPosition") return t("groupAirQualityRejectedUncertainPosition", "posizione incerta (buco GPS)");
        if (reason === "stationary") return t("groupAirQualityRejectedStationary", "in sosta");
        return "";
    };

    /** Testo del pallino: grandezza e valore, i `details` di chi chiama, stima ed esclusione. */
    const tooltipText = (dot, ...details) => {
        if (dot.value == null) return null;
        const value = Number(dot.value).toLocaleString(i18n.language, { maximumFractionDigits: 2 });
        const lines = [[`${metricLabel}: ${value}${unit ? " " + unit : ""}`, ...details].join(" · ")];
        if (dot.estimated) lines.push(t("groupAirQualityEstimatedPosition", "posizione stimata"));
        if (dot.rejected) lines.push(rejectedLabel(dot.rejected));
        return lines.join("\n");
    };

    return {
        selectedMetricKey,
        setSelectedMetricKey,
        countByMetric,
        availableMetrics,
        metricLabel,
        unit,
        result,
        results,
        segments,
        dots,
        tooltipText,
    };
}

/**
 * Bottoni delle grandezze, mappa con il percorso di gruppo colorato, campioni, partenza e arrivo,
 * legenda delle fasce e, sotto la mappa per non coprirla, come leggere linea e pallini.
 * `aggregation` e' il risultato di {@link useGroupAirQualityAggregation} sugli stessi dati;
 * la mappa inquadra tutti i punti di `pieces`, `start` e `end` sono partenza e arrivo del gruppo.
 */
export default function GroupAirQualityMap({ pieces, start, end, aggregation, getTooltipText, height = "70vh" }) {
    const { t } = useTranslation();
    const { selectedMetricKey, setSelectedMetricKey, countByMetric, metricLabel, unit, segments, dots } = aggregation;

    const bounds = useMemo(
        () => (pieces || []).flatMap(piece => piece.points.map(p => ({ lat: p.lat, lng: p.lng }))),
        [pieces]
    );
    // Memoizzato: GoogleMap confronta il centro per riferimento e ricentrerebbe a ogni render.
    const center = useMemo(() => (bounds.length > 0 ? bounds[0] : undefined), [bounds]);

    return (
        <>
            <Box sx={{ mb: 2 }}>
                <MetricButtons
                    selectedMetricKey={selectedMetricKey}
                    onSelect={setSelectedMetricKey}
                    countByMetric={countByMetric}
                    missingTitle={t("environmentalDataMetricMissing", "Nessuna campionatura di questa grandezza nella sessione")}
                />
            </Box>

            <Box sx={{ width: "100%", height, position: "relative" }}>
                <MapContainer center={center} zoom={14}>
                    <EnvironmentOverlay segments={segments} dots={dots} bounds={bounds} getTooltipText={getTooltipText} />
                    {start && (
                        <AdvancedMarker
                            position={{ lat: start.lat, lng: start.lng }}
                            icon={{ url: startIcon, scaledSize: { width: 32, height: 32 } }}
                        />
                    )}
                    {end && (
                        <AdvancedMarker
                            position={{ lat: end.lat, lng: end.lng }}
                            icon={{ url: finishIcon, scaledSize: { width: 32, height: 32 } }}
                        />
                    )}
                </MapContainer>
                <EnvironmentLegend metricKey={selectedMetricKey} metricLabel={metricLabel} unit={unit} />
            </Box>

            <Box sx={{ mt: 1 }}>
                <GroupAirQualityMapKey />
            </Box>
        </>
    );
}
